import React from 'react';
import { Link } from 'react-router-dom';
import PublicPageLayout from '../components/landing-page/PublicPageLayout';

export default function PrivacyPage() {
  return (
    <PublicPageLayout
      title='Privacy Policy'
      subtitle='How we handle personal information for our SAAS products: uploaded pricing tables, install code, API delivery, and visitor session data used to optimize revenue.'
    >
      <p className='text-sm text-gray-400'>Last updated: March 4, 2025</p>

      <p className='text-sm text-gray-300'>
      </p>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>1. What PricingTable AI does (and how that relates to privacy)</h2>
        <p>
          PricingTable AI is a subscription web application for <span className='font-medium text-white'>SaaS and similar businesses</span>{' '}
          that want to test which pricing combination works best for their products. Customers upload a pricing table for their product, define{' '}
          <span className='font-medium text-white'>candidate prices</span> to try for each tier and billing period (for example, a table
          with three tiers and monthly vs. annual billing implies multiple underlying prices and many possible combinations), and our
          system generates <span className='font-medium text-white'>distinct pricing table variants</span>—each with a unique
          combination of those prices applied to their template.
        </p>
        <p>
          Customers install a <span className='font-medium text-white'>snippet of code</span> (from our install flow) on their own
          marketing or signup site. That code calls our <span className='font-medium text-white'>API</span>, which returns one of the
          generated pricing table versions so a given visitor sees a specific price combination. The same integration{' '}
          <span className='font-medium text-white'>records how visitors interact</span> with the pricing experience (for example,
          views, selections, or conversion-related events) and sends that data to our backend so we can store it alongside session and
          experiment context.
        </p>
        <p>
          We run <span className='font-medium text-white'>analytics and algorithms</span> on that visitor behavior (outcomes such as
          conversion or revenue signals) relative to which price combination was shown, to help identify combinations that perform
          better for each customer&apos;s product. This Privacy Policy explains what personal information is involved when we operate
          that service, our own website and app, and related marketing (such as waitlists for upcoming offerings).
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>2. Scope and who this policy covers</h2>
        <p>
          This Privacy Policy explains how PricingTable AI (&quot;<span className='font-medium text-white'>we</span>,&quot; &quot;
          <span className='font-medium text-white'>us</span>,&quot; or &quot;<span className='font-medium text-white'>our</span>&quot;)
          collects, uses, discloses, and otherwise processes personal information in connection with our websites, hosted application,
          APIs, install instructions, documentation, marketing, and related offerings (collectively, the &quot;
          <span className='font-medium text-white'>Services</span>&quot;).
        </p>
        <p>
          The Services are intended for <span className='font-medium text-white'>businesses and professionals</span> who subscribe
          to the application and configure pricing experiments. Unless we say otherwise, we treat information we collect in those
          contexts as relating to individuals acting on behalf of a company (account owners, billing contacts, developers implementing
          the install snippet), not for unrelated personal or household purposes.
        </p>
        <p>
          By using the Services or submitting personal information to us, you acknowledge the practices described in this Privacy
          Policy and our <Link to='/cookies' className='text-yellow-400 underline hover:text-yellow-300'>Cookie Policy</Link>, where
          applicable. Our <Link to='/terms-of-service' className='text-yellow-400 underline hover:text-yellow-300'>Terms of Service</Link>{' '}
          govern your use of the product.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>3. Customer accounts vs. visitors on customer websites</h2>
        <p>
          We process two broad categories of information, which matter for privacy and for who you should contact with questions:
        </p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Subscriber and team data:</span> information about people who create accounts,
            pay for subscriptions, upload pricing tables, configure candidate prices and experiments, obtain API or embed
            credentials, and use our dashboard. This Privacy Policy describes our practices for that data as{' '}
            <span className='font-medium text-white'>controller</span> (or business) in the usual sense, subject to your contract with
            us.
          </li>
          <li>
            <span className='font-medium text-white'>End-user (visitor) data from the install snippet:</span> when someone visits{' '}
            <em>your</em> site where our customer&apos;s code runs, we may process technical identifiers, session or cookie-related
            data, interaction events, and inferred outcomes tied to <span className='font-medium text-white'>which price combination</span>{' '}
            was served—so we can attribute behavior to experiment arms and run optimization. You (the customer) generally decide what
            to disclose to your visitors under your privacy policy; our processing of that data on your behalf may be governed by your
            agreement with us and, where applicable, a data processing addendum, in addition to this policy where we describe the
            processing at a high level.
          </li>
        </ul>
        <p>
          If you landed on a SaaS company&apos;s site—not ours—and have questions about tracking or pricing tests there, contact that
          company. If you use our app or website as a subscriber or prospect, this policy and our Support channel apply.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>4. Personal information we collect</h2>
        <h3 className='text-lg font-medium text-white'>4.1 Information subscribers and business contacts provide</h3>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Account and contact details:</span> name, work email, company name, role, phone
            number, address or region, and similar identifiers when you register, subscribe, join a waitlist (for example on our{' '}
            <Link to='/upcoming' className='text-yellow-400 underline hover:text-yellow-300'>Upcoming</Link> page), or communicate with
            us.
          </li>
          <li>
            <span className='font-medium text-white'>Credentials:</span> username, password, or authentication tokens (we may use
            third-party identity providers where offered).
          </li>
          <li>
            <span className='font-medium text-white'>Billing and payment:</span> billing address, subscription plan, and payment
            transaction metadata. Card or bank details are typically collected by our payment processors; we receive only what they
            need to confirm payment status.
          </li>
          <li>
            <span className='font-medium text-white'>Pricing and experiment configuration:</span> uploaded pricing tables (for example
            HTML/CSS/JS or structured markup), lists of candidate prices per tier and billing period, experiment identifiers, API or
            embed keys, and integration settings—everything needed to generate variants and attribute traffic to a combination.
          </li>
          <li>
            <span className='font-medium text-white'>Communications:</span> messages via support, feedback forms, email, or official
            social pages.
          </li>
          <li>
            <span className='font-medium text-white'>Marketing preferences:</span> choices about newsletters, webinars, or promotional
            communications.
          </li>
        </ul>

        <h3 className='text-lg font-medium text-white'>4.2 Information collected automatically (our sites and product)</h3>
        <p>When you use our marketing site or logged-in application, we and our service providers may log:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Device and technical data:</span> browser type and version, operating system,
            device type, screen resolution, language, IP address, approximate location derived from IP, and unique device or
            advertising identifiers where permitted.
          </li>
          <li>
            <span className='font-medium text-white'>Usage and activity data:</span> pages viewed, features used (including upload and
            install flows), navigation paths, timestamps, referring URLs, session duration, errors, and diagnostic logs.
          </li>
          <li>
            <span className='font-medium text-white'>Email engagement:</span> opens or clicks when we use pixels in campaigns.
          </li>
        </ul>
        <p>
          We use <span className='font-medium text-white'>cookies, local storage, pixels, and similar technologies</span> as described
          in our <Link to='/cookies' className='text-yellow-400 underline hover:text-yellow-300'>Cookie Policy</Link>.
        </p>

        <h3 className='text-lg font-medium text-white'>4.3 Data from the install snippet and API (visitor-side)</h3>
        <p>
          To operate price tests, we process data generated when the snippet runs on a customer&apos;s site, which may include:
        </p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Session and assignment data:</span> identifiers or tokens that let us recognize a
            browser session, remember which <span className='font-medium text-white'>price combination</span> or variant was served,
            and correlate events over time for analysis.
          </li>
          <li>
            <span className='font-medium text-white'>Interaction and outcome events:</span> signals such as which pricing options were
            viewed, selected, or converted—stored so we (and the customer) can compare performance across combinations and run
            optimization algorithms.
          </li>
          <li>
            <span className='font-medium text-white'>Technical context:</span> timestamps, page URL, user agent, and similar metadata
            needed for debugging, fraud prevention, and reliable delivery of the correct variant.
          </li>
        </ul>
        <p>
          Depending on how the customer configures their site and what they collect elsewhere, other personal information could be
          present in page context; our product is designed around pricing experimentation and session-level analytics, not to replace
          the customer&apos;s own disclosures to their users.
        </p>

        <h3 className='text-lg font-medium text-white'>4.4 Information from third parties</h3>
        <p>
          We may receive personal information from enrichment providers, public sources, event partners, social platforms when you
          interact with our pages, or referrals—combined with what you provide where allowed by law and contract.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>5. How we use personal information</h2>
        <p>We use personal information for purposes including:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Delivering the core product:</span> authenticating subscribers; storing pricing
            tables and candidate prices; generating table variants with unique price combinations; serving variants through the API;
            recording visitor interactions and session data; attributing outcomes to combinations; and presenting results so customers
            can see which combinations perform better on metrics such as conversion and revenue.
          </li>
          <li>
            <span className='font-medium text-white'>Algorithms and analytics:</span> aggregating and analyzing experiment data
            (including modeling which independent variables—price combinations—relate to dependent variables such as clicks,
            sign-ups, or purchase outcomes) to improve reported insights and product behavior.
          </li>
          <li>
            <span className='font-medium text-white'>Security and integrity:</span> abuse detection, fraud prevention, enforcing
            limits, and protecting accounts and API access.
          </li>
          <li>
            <span className='font-medium text-white'>Support and improvement:</span> troubleshooting, product analytics, research and
            development.
          </li>
          <li>
            <span className='font-medium text-white'>Communications:</span> transactional messages (billing, security, install/API
            notices), service announcements, and—where permitted—marketing. You can opt out of marketing as described below.
          </li>
          <li>
            <span className='font-medium text-white'>Aggregated and de-identified data:</span> statistics that do not identify
            individuals, used for benchmarking, reporting, or improving the Services.
          </li>
          <li>
            <span className='font-medium text-white'>Legal and compliance:</span> responding to lawful requests, protecting rights,
            and defending claims.
          </li>
        </ul>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>6. Legal bases for processing (EEA, UK, and similar)</h2>
        <p>
          Where required, we rely on legal bases such as: <span className='font-medium text-white'>contract</span> (providing the
          subscription and API); <span className='font-medium text-white'>legitimate interests</span> (securing the Services, product
          improvement, understanding usage, marketing balanced against your rights); <span className='font-medium text-white'>consent</span>{' '}
          (where we ask for it, e.g. certain cookies or marketing); and <span className='font-medium text-white'>legal obligation</span>.
          Processing of end-user data through the snippet may additionally depend on the customer&apos;s lawful basis and instructions
          where we act as processor.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>7. How we share personal information</h2>
        <p>We may share personal information with:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Service providers:</span> hosting, databases (including storage of session and
            event records), analytics, email, support tooling, security, fraud prevention, and payment processing—under confidentiality
            and security terms.
          </li>
          <li>
            <span className='font-medium text-white'>Professional advisors:</span> lawyers, auditors, insurers, and bankers where
            necessary.
          </li>
          <li>
            <span className='font-medium text-white'>Authorities and others:</span> when we believe in good faith disclosure is
            required or appropriate for legal compliance, safety, or enforcement of our terms.
          </li>
          <li>
            <span className='font-medium text-white'>Business transfers:</span> mergers, acquisitions, financings, or asset sales,
            subject to appropriate protections.
          </li>
          <li>
            <span className='font-medium text-white'>Affiliates:</span> companies under common control, consistent with this policy.
          </li>
        </ul>
        <p>
          We do not sell personal information for money. Some cookie or analytics arrangements may be treated as a &quot;sale&quot;
          or &quot;sharing&quot; under certain U.S. state laws—see Section 10.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>8. International transfers</h2>
        <p>
          We may process personal information in the United States and other countries where we or our suppliers operate. Where
          transfers from the EEA, UK, Switzerland, or similar regions require safeguards, we use mechanisms such as standard
          contractual clauses or other approved arrangements. Contact us for more detail.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>9. Your privacy choices and rights</h2>
        <p>
          Depending on your location, you may have rights to access, correct, delete, or export personal information; restrict or
          object to processing; withdraw consent; or complain to a supervisory authority. Automated decision-making rights may apply
          where local law provides them.
        </p>
        <p>
          Contact us through <Link to='/support' className='text-yellow-400 underline hover:text-yellow-300'>Support</Link>. We may
          verify your identity before responding. We will not discriminate against you for exercising applicable rights.
        </p>
        <p>
          <span className='font-medium text-white'>Marketing:</span> unsubscribe via links in emails. You may still receive
          account, billing, security, and legal notices.
        </p>
        <p>
          <span className='font-medium text-white'>Cookies:</span> see our{' '}
          <Link to='/cookies' className='text-yellow-400 underline hover:text-yellow-300'>Cookie Policy</Link>.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>10. U.S. state privacy notices (including California)</h2>
        <p>
          Residents of California and certain other states may have rights to know categories of personal information collected, to
          request deletion or correction, and to opt out of certain &quot;sales&quot; or &quot;sharing&quot; for cross-context
          behavioral or targeted advertising, as defined by applicable law.
        </p>
        <p>
          We describe collection, purposes, and sharing in this policy—including subscriber account data, configuration and pricing
          content, and snippet/API-derived session and interaction data used for experimentation. We do not knowingly sell or share
          personal information of minors under 16 for cross-context behavioral advertising.
        </p>
        <p>
          Submit requests via <Link to='/support' className='text-yellow-400 underline hover:text-yellow-300'>Support</Link>. For
          cookie-based choices, see our Cookie Policy. You may have appeal rights if a request is denied.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>11. Job applicants</h2>
        <p>
          If you apply for a role, we collect what you submit (resume, contact details, history, references) and use it to evaluate
          candidacy and comply with law, retaining it as required by our schedule and legal obligations.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>12. Retention</h2>
        <p>
          We retain personal information as long as needed to provide the Services, run and report on experiments, resolve disputes,
          defend claims, and meet legal, tax, and accounting requirements. Experiment and session records may be kept for a period
          sufficient to compute reliable metrics and support historical comparisons, then deleted or aggregated according to
          customer settings, contract, and law.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>13. Other sites and services</h2>
        <p>
          Customer sites where the snippet runs, and third-party links from our properties, are governed by their own policies. We are
          not responsible for third-party practices.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>14. Security</h2>
        <p>
          We use technical, organizational, and administrative measures designed to protect personal information. No online service is
          perfectly secure; protect your account credentials and API keys.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>15. Children</h2>
        <p>
          The Services are not directed to children under 16 (or the age required by local law). If we learn we have collected
          information from a child inappropriately, we will take steps to delete it.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>16. Changes to this Privacy Policy</h2>
        <p>
          We may update this policy, post the new version here, and change the &quot;Last updated&quot; date. Material changes may
          warrant additional notice. Continued use after the effective date may constitute acceptance where allowed by law.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>17. Contact us</h2>
        <p>
          Questions or rights requests: <Link to='/support' className='text-yellow-400 underline hover:text-yellow-300'>Support</Link>.
          Cookies: <Link to='/cookies' className='text-yellow-400 underline hover:text-yellow-300'>Cookie Policy</Link>.
        </p>
      </section>
    </PublicPageLayout>
  );
}
